import {
    CreateObjectiveTargetInput,
    GetObjectiveQuery,
    ObjectiveSource,
    ObjectiveStatus,
    ObjectiveTargetStatus,
} from "@generated/client/graphql";

export const canValidateObjective = (
    objective: GetObjectiveQuery["objective"],
) => {
    const hasName = objective.name !== "";
    const hasEntity = objective.entityId != null;
    const hasSubtheme = objective.subthemeId != null;
    const hasTheme = objective.themeId != null;

    const hasIndicators = objective.indicatorId != null;
    const areAllTargetsFilled =
        !!objective.targets &&
        objective.targets.length > 0 &&
        objective.targets.every((target) => {
            const hasDate = target.date != null;
            /**
             * Qualitative targets don't have a value
             */
            if (!hasIndicators) return hasDate;

            const hasValue = target.value != null;
            return hasDate && hasValue;
        });

    return (
        hasName && hasEntity && hasSubtheme && hasTheme && areAllTargetsFilled
    );
};

export function makeOptimisticObjectiveTargetsUpdateOnTargetDelete(
    objectiveId: string,
) {
    return function (patch: string, previousQuery?: GetObjectiveQuery) {
        if (!previousQuery) return previousQuery;
        if (!objectiveId) throw new Error("No objectiveId provided");
        const previousTargets = previousQuery.objective?.targets;
        if (!previousTargets) throw new Error("No previous targets found");
        const updatedTargets = previousTargets.filter(
            (target) => target.id !== patch,
        );
        const updatedQuery: GetObjectiveQuery = {
            ...previousQuery,
            objective: {
                ...previousQuery.objective,
                targets: updatedTargets,
            },
        };
        return updatedQuery;
    };
}

export function makeOptimisticObjectiveTargetsUpdateOnManyTargetsDelete(
    objectiveId: string,
) {
    return function (patch: string[], previousQuery?: GetObjectiveQuery) {
        if (!previousQuery) return previousQuery;
        if (!objectiveId) throw new Error("No objectiveId provided");
        const previousTargets = previousQuery.objective?.targets;
        if (!previousTargets) throw new Error("No previous targets found");
        const updatedTargets = previousTargets.filter(
            (target) => !patch.includes(target.id),
        );
        const updatedQuery: GetObjectiveQuery = {
            ...previousQuery,
            objective: {
                ...previousQuery.objective,
                targets: updatedTargets,
            },
        };
        return updatedQuery;
    };
}

export function makeOptimisticObjectiveTargetsUpdateOnTargetCreate(
    objectiveId: string,
) {
    return function (
        patch: CreateObjectiveTargetInput,
        previousQuery?: GetObjectiveQuery,
    ) {
        if (!previousQuery) return previousQuery;
        if (!objectiveId) throw new Error("No objectiveId provided");
        const previousTargets = previousQuery.objective?.targets;
        if (!previousTargets) throw new Error("No previous targets found");
        const updatedQuery: GetObjectiveQuery = {
            ...previousQuery,
            objective: {
                ...previousQuery.objective,
                targets: [
                    ...previousTargets,
                    {
                        ...patch,
                        id: "1",
                        value: patch?.value ?? {},
                        status: ObjectiveTargetStatus.Ongoing,
                        date: patch?.date ?? new Date(),
                        source: ObjectiveSource.Manual,
                    },
                ],
            },
        };
        return updatedQuery;
    };
}

export function isObjectiveEditable(objective: GetObjectiveQuery["objective"]) {
    return objective.status === ObjectiveStatus.Draft;
}
